import { Tag } from 'antd';
import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

import { companyColors } from 'apps/investment/constants';
import {
  orderSideColors,
  orderSideLabels,
} from 'apps/investment/constants/programOrder';
import {
  userProgramStatus,
  userProgramStatusColors,
} from 'apps/investment/constants/userProgram';
import {
  currencyCell,
  dateTimeCell,
  linkCell,
  numberCell,
} from 'utils/cell-formatter';
import { getChangeValueColor } from 'utils/color';
import { formatCurrency, formatUnitQuantity } from 'utils/formatAmount';
import { formatDate } from 'utils/formatDate';

export const columnsGenerator = () => [
  {
    accessorKey: 'id',
    header: '',
    cell: ({ row }) =>
      linkCell({
        getValue: () => row.original.user_id,
        path: '/investment/portfolios',
      }),
  },
  {
    header: 'Khách hàng',
    accessorKey: 'user',
    cell: ({ getValue }) => {
      const user = getValue();
      return (
        <div>
          <div>{user.name ?? ''}</div>
          <div>{user.email ?? ''}</div>
        </div>
      );
    },
  },

  {
    accessorKey: 'buy_value',
    header: 'Vốn mua hiện tại',
    cell: ({ row, getValue }) => {
      const value = getValue();
      const companyValue = row.original.company_buy_value;

      return (
        <div className="text-right">
          {formatCurrency(value)}
          {companyValue > 0 && (
            <div>
              (Trong đó, vốn doanh nghiệp đóng góp:{' '}
              {formatCurrency(companyValue)} )
            </div>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: 'unrealized_gain_loss_percentage',
    header: 'ROI (%)',
    cell: ({ getValue }) => {
      const value = getValue();
      return (
        <div className="flex justify-end font-bold">
          <Tag color={getChangeValueColor(value)}>
            {formatUnitQuantity(value)}
          </Tag>
        </div>
      );
    },
  },
  {
    accessorKey: 'market_value',
    header: 'AUM',
    cell: ({ getValue, row }) => {
      const value = getValue();
      const companyMarketValue = row.original.company_market_value;
      return (
        <div className="text-right">
          {formatCurrency(value)}
          {companyMarketValue > 0 && (
            <div>
              (Trong đó, giá trị doanh nghiệp đóng góp:{' '}
              {formatCurrency(companyMarketValue)} )
            </div>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: 'created_at',
    header: 'Created',
    cell: dateTimeCell,
  },
  {
    accessorKey: 'updated_at',
    header: 'Updated',
    cell: dateTimeCell,
  },
];

export const portfolioFundColumns = [
  {
    header: 'Sản phẩm',
    accessorKey: 'product_code',
  },
  {
    header: 'Nhà phân phối',
    accessorKey: 'trading_account_manager',
    meta: {
      className: 'text-center',
    },
    cell: ({ getValue }) => {
      const value = getValue();
      return <Tag color={companyColors[value]}>{value}</Tag>;
    },
  },

  {
    header: () => <div className="text-right"> Số lượng</div>,
    accessorKey: 'quantity',
    cell: numberCell,
  },
  {
    header: () => <div className="text-right">SL có thể bán</div>,
    accessorKey: 'available_quantity',
    cell: numberCell,
  },
  {
    header: () => <div className="text-right">SL chờ bán</div>,
    accessorKey: 'lock_quantity',
    cell: numberCell,
  },
  {
    header: () => <div className="text-right">Giá mua TB</div>,
    accessorKey: 'avg_buy_price',
    cell: numberCell,
  },
  {
    header: () => <div className="text-right">Vốn mua</div>,
    accessorKey: 'cert_subscription_amount',
    cell: currencyCell,
  },
  {
    header: () => <div className="text-right">AUM</div>,
    accessorKey: 'market_value',
    cell: currencyCell,
  },
  {
    header: () => <div className="text-right">P&L</div>,
    accessorKey: 'unrealized_gain_loss_amount',
    cell: ({ getValue, row }) => {
      const amount = getValue();
      const percentage = row.original.unrealized_gain_loss_percentage;
      const isPositive = amount >= 0;
      return (
        <div className="text-right">
          <div
            className={`font-semibold ${
              isPositive ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {isPositive ? '+' : ''}
            {formatCurrency(amount)}
          </div>
          <div
            className={`text-xs ${
              isPositive ? 'text-green-500' : 'text-red-500'
            }`}
          >
            ({isPositive ? '+' : ''}
            {formatUnitQuantity(percentage)}%)
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: 'created_at',
    header: 'Created',
    cell: dateTimeCell,
  },
  {
    accessorKey: 'updated_at',
    header: 'Updated',
    cell: dateTimeCell,
  },
];

export const portfolioStockColumns = [
  {
    header: 'Sản phẩm',
    accessorKey: 'stock_symbol',
  },
  {
    header: () => <div className="text-right">Số lượng</div>,
    accessorKey: 'quantity',
    cell: numberCell,
  },
  {
    header: () => <div className="text-right">SL có thể bán</div>,
    accessorKey: 'available_quantity',
    cell: numberCell,
  },
  {
    header: () => <div className="text-right">SL chờ bán</div>,
    accessorKey: 'lock_quantity',
    cell: numberCell,
  },
  {
    header: () => <div className="text-right">SL chờ về</div>,
    accessorKey: 'incoming_quantity',
    cell: numberCell,
  },
  {
    header: () => <div className="text-right">Giá mua TB</div>,
    accessorKey: 'avg_buy_price',
    cell: numberCell,
  },
  {
    header: () => <div className="text-right">Vốn mua</div>,
    accessorKey: 'subscription_amount',
    cell: currencyCell,
  },
  {
    header: () => <div className="text-right">AUM</div>,
    accessorKey: 'market_value',
    cell: currencyCell,
  },
  {
    header: () => <div className="text-right">P&L</div>,
    accessorKey: 'unrealized_gain_loss_amount',
    cell: ({ getValue, row }) => {
      const amount = getValue();
      const percentage = row.original.unrealized_gain_loss_percentage;
      const isPositive = amount >= 0;
      return (
        <div className="text-right">
          <div
            className={`font-semibold ${
              isPositive ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {isPositive ? '+' : ''}
            {formatCurrency(amount)}
          </div>
          <div
            className={`text-xs ${
              isPositive ? 'text-green-500' : 'text-red-500'
            }`}
          >
            ({isPositive ? '+' : ''}
            {formatUnitQuantity(percentage)}%)
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: 'created_at',
    header: 'Created',
    cell: dateTimeCell,
  },
  {
    accessorKey: 'updated_at',
    header: 'Updated',
    cell: dateTimeCell,
  },
];

export const portfolioFlexColumns = [
  {
    header: 'Sản phẩm',
    accessorKey: 'product_code',
    cell: ({ getValue, row }) => {
      const symbol = getValue();
      const program = row.original.employee_program;
      const side = row.original.order_side;
      return (
        <>
          <div className="mb-1">
            <Tag color={orderSideColors[side]}>{orderSideLabels[side]}</Tag>
            {symbol}
          </div>
          <Link to={`/futureflex/employee-programs/${program?.id}`}>
            {program?.code}
          </Link>
        </>
      );
    },
  },

  {
    header: () => <div className="text-right">Số lượng</div>,
    accessorKey: 'quantity',
    cell: numberCell,
  },
  {
    header: () => <div className="text-right">SL có thể bán</div>,
    accessorKey: 'available_quantity',
    cell: numberCell,
  },
  {
    header: () => <div className="text-right">SL đang khóa</div>,
    accessorKey: 'lock_quantity',
    cell: numberCell,
  },
  {
    header: () => <div className="text-right">Giá mua TB</div>,
    accessorKey: 'avg_buy_price',
    cell: currencyCell,
  },
  {
    header: () => <div className="text-right">Vốn mua</div>,
    accessorKey: 'cert_subscription_amount',
    cell: currencyCell,
  },
  {
    header: () => <div className="text-right">AUM</div>,
    accessorKey: 'market_value',
    cell: currencyCell,
  },
  {
    header: () => <div className="text-right">P&L</div>,
    accessorKey: 'unrealized_gain_loss_amount',
    cell: ({ getValue, row }) => {
      const amount = getValue();
      const percentage = row.original.unrealized_gain_loss_percentage;
      const isPositive = amount >= 0;
      return (
        <div className="text-right">
          <div
            className={`font-semibold ${
              isPositive ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {isPositive ? '+' : ''}
            {formatCurrency(amount)}
          </div>
          <div
            className={`text-xs ${
              isPositive ? 'text-green-500' : 'text-red-500'
            }`}
          >
            ({isPositive ? '+' : ''}
            {formatUnitQuantity(percentage)}%)
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: 'created_at',
    header: 'Created',
    cell: dateTimeCell,
  },
  {
    accessorKey: 'updated_at',
    header: 'Updated',
    cell: dateTimeCell,
  },
];

export const stockOrderColumns = [
  {
    accessorKey: 'order_date',
    header: 'Order Date',
    cell: dateTimeCell,
  },
  {
    accessorKey: 'order_side',
    header: 'Side',
    meta: { className: 'text-center' },
    cell: ({ getValue }) => {
      const value = getValue();
      const colors = { buy: 'green', sell: 'red' };
      const labels = { buy: 'Mua', sell: 'Bán' };
      return <Tag color={colors[value]}>{labels[value] || value}</Tag>;
    },
  },
  {
    accessorKey: 'symbol',
    header: 'Symbol',
  },
  {
    accessorKey: 'price',
    header: () => <div className="text-right">Price</div>,
    cell: currencyCell,
  },
  {
    accessorKey: 'volume',
    header: () => <div className="text-right">Volume</div>,
    cell: numberCell,
  },
  {
    accessorKey: 'matched_volume',
    header: () => <div className="text-right">Matched</div>,
    cell: numberCell,
  },
  {
    header: 'Status',
    accessorKey: 'status',
    meta: { className: 'text-center' },
    cell: ({ row, getValue }) => {
      const status = getValue();
      const label = row.original.status_label;
      const colors = {
        1: 'blue',
        2: 'orange',
        3: 'green',
        4: 'red',
        5: 'default',
      };
      return <Tag color={colors[status]}>{label}</Tag>;
    },
  },
  {
    accessorKey: 'created_at',
    header: 'Created',
    cell: dateTimeCell,
  },
];

export const stockTransferColumns = [
  {
    accessorKey: 'date',
    header: 'Ngày',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? new Date(value).toLocaleDateString('vi-VN') : '-';
    },
  },
  {
    accessorKey: 'symbol',
    header: 'Mã CK',
    cell: ({ getValue }) => {
      const value = getValue();
      return value || '-';
    },
  },
  {
    accessorKey: 'quantity',
    header: () => <div className="text-right">Số lượng</div>,
    cell: ({ getValue }) => {
      const value = getValue();
      const isPositive = value >= 0;
      return (
        <div className="text-right">
          <Tag color={isPositive ? 'green' : 'red'}>
            {isPositive ? '+' : ''}
            {formatUnitQuantity(value)}
          </Tag>
        </div>
      );
    },
  },
  {
    accessorKey: 'avg_buy_price',
    header: () => <div className="text-right">Giá mua TB</div>,
    cell: currencyCell,
  },
  {
    accessorKey: 'subscription_amount',
    header: () => <div className="text-right">Vốn mua</div>,
    cell: currencyCell,
  },
  {
    accessorKey: 'market_value',
    header: () => <div className="text-right">Giá trị TT</div>,
    cell: currencyCell,
  },
  {
    accessorKey: 'pnl_amount',
    header: () => <div className="text-right">P&L</div>,
    cell: ({ getValue, row }) => {
      const amount = getValue();
      const percentage = row.original.pnl_percentage;
      const isPositive = amount >= 0;
      return (
        <div className="text-right">
          <div
            className={`font-semibold ${
              isPositive ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {isPositive ? '+' : ''}
            {formatCurrency(amount)}
          </div>
          <div
            className={`text-xs ${
              isPositive ? 'text-green-500' : 'text-red-500'
            }`}
          >
            ({isPositive ? '+' : ''}
            {formatUnitQuantity(percentage)}%)
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: 'created_at',
    header: 'Tạo lúc',
    cell: dateTimeCell,
  },
];

export const goalColumns = [
  {
    header: 'Tên mục tiêu',
    accessorKey: 'program_name',
    cell: ({ getValue, row }) => (
      <Link
        to={`/investment/user-programs/${row.original.id}/portfolio`}
        className="font-medium text-blue-600 hover:text-blue-800"
      >
        {getValue()}
      </Link>
    ),
  },
  {
    header: 'Trạng thái',
    accessorKey: 'status',
    meta: { className: 'text-center' },
    cell: ({ getValue }) => {
      const status = getValue();
      return status ? (
        <Tag color={userProgramStatusColors[status]}>
          {userProgramStatus[status] || status}
        </Tag>
      ) : null;
    },
  },
  {
    header: 'Ngày bắt đầu',
    accessorKey: 'start_date',
    cell: ({ getValue }) => formatDate(getValue()) || '-',
  },
  {
    header: 'Ngày kết thúc',
    accessorKey: 'end_date',
    cell: ({ getValue }) => formatDate(getValue()) || '-',
  },
  {
    header: '',
    accessorKey: 'id',
    cell: ({ getValue }) => (
      <Link
        to={`/investment/user-programs/${getValue()}/portfolio`}
        className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
      >
        Xem danh mục <ExternalLink className="size-3" />
      </Link>
    ),
  },
];

const GoalLink = ({ goalId }: { goalId: string }) => (
  <Link
    to={`/investment/user-programs/${goalId}/portfolio`}
    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 text-xs font-medium transition-colors"
  >
    <ExternalLink className="size-3" />
    Xem mục tiêu
  </Link>
);

export const portfolioFundGoalColumns = [
  {
    header: 'Mục tiêu',
    accessorKey: 'goal_id',
    cell: ({ getValue }) => <GoalLink goalId={getValue()} />,
  },
  {
    header: 'Sản phẩm',
    accessorKey: 'product_code',
  },
  {
    header: 'Nhà phân phối',
    accessorKey: 'trading_account_manager',
    meta: { className: 'text-center' },
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? <Tag color={companyColors[value]}>{value}</Tag> : '-';
    },
  },
  {
    header: () => <div className="text-right">Số lượng</div>,
    accessorKey: 'quantity',
    cell: numberCell,
  },
  {
    header: () => <div className="text-right">SL có thể bán</div>,
    accessorKey: 'available_quantity',
    cell: numberCell,
  },
  {
    header: () => <div className="text-right">SL chờ bán</div>,
    accessorKey: 'lock_quantity',
    cell: numberCell,
  },
  {
    header: () => <div className="text-right">Giá mua TB</div>,
    accessorKey: 'avg_buy_price',
    cell: numberCell,
  },
  {
    header: () => <div className="text-right">Vốn mua</div>,
    accessorKey: 'cert_subscription_amount',
    cell: currencyCell,
  },
  {
    header: () => <div className="text-right">AUM</div>,
    accessorKey: 'market_value',
    cell: currencyCell,
  },
  {
    header: () => <div className="text-right">P&L</div>,
    accessorKey: 'unrealized_gain_loss_amount',
    cell: ({ getValue, row }) => {
      const amount = getValue();
      const percentage = row.original.unrealized_gain_loss_percentage;
      const isPositive = amount >= 0;
      return (
        <div className="text-right">
          <div
            className={`font-semibold ${
              isPositive ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {isPositive ? '+' : ''}
            {formatCurrency(amount)}
          </div>
          <div
            className={`text-xs ${
              isPositive ? 'text-green-500' : 'text-red-500'
            }`}
          >
            ({isPositive ? '+' : ''}
            {formatUnitQuantity(percentage)}%)
          </div>
        </div>
      );
    },
  },
];

export const portfolioStockGoalColumns = [
  {
    header: 'Mục tiêu',
    accessorKey: 'goal_id',
    cell: ({ getValue }) => <GoalLink goalId={getValue()} />,
  },
  {
    header: 'Mã CK',
    accessorKey: 'stock_symbol',
  },
  {
    header: () => <div className="text-right">Số lượng</div>,
    accessorKey: 'quantity',
    cell: numberCell,
  },
  {
    header: () => <div className="text-right">SL có thể bán</div>,
    accessorKey: 'available_quantity',
    cell: numberCell,
  },
  {
    header: () => <div className="text-right">Giá mua TB</div>,
    accessorKey: 'avg_buy_price',
    cell: numberCell,
  },
  {
    header: () => <div className="text-right">Vốn mua</div>,
    accessorKey: 'subscription_amount',
    cell: currencyCell,
  },
  {
    header: () => <div className="text-right">AUM</div>,
    accessorKey: 'market_value',
    cell: currencyCell,
  },
  {
    header: () => <div className="text-right">P&L</div>,
    accessorKey: 'unrealized_gain_loss_amount',
    cell: ({ getValue, row }) => {
      const amount = getValue();
      const percentage = row.original.unrealized_gain_loss_percentage;
      const isPositive = amount >= 0;
      return (
        <div className="text-right">
          <div
            className={`font-semibold ${
              isPositive ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {isPositive ? '+' : ''}
            {formatCurrency(amount)}
          </div>
          <div
            className={`text-xs ${
              isPositive ? 'text-green-500' : 'text-red-500'
            }`}
          >
            ({isPositive ? '+' : ''}
            {formatUnitQuantity(percentage)}%)
          </div>
        </div>
      );
    },
  },
];

export const securityUserStocksColumns = [
  {
    header: 'Mã CK',
    accessorKey: 'stock_symbol',
  },
  {
    header: () => <div className="text-right">Số lượng</div>,
    accessorKey: 'quantity',
    cell: ({ getValue, row }) => {
      const total = getValue();
      const available = row.original.available_quantity;
      const locked = row.original.lock_quantity;
      return (
        <div className="text-right">
          <div className="font-semibold">{formatUnitQuantity(total)}</div>
          <div className="text-xs text-gray-500">
            KD: {formatUnitQuantity(available)} | Khóa:{' '}
            {formatUnitQuantity(locked)}
          </div>
        </div>
      );
    },
  },
  {
    header: () => <div className="text-right">Giá</div>,
    accessorKey: 'current_price',
    cell: ({ getValue, row }) => {
      const current = getValue();
      const avg = row.original.avg_buy_price;
      return (
        <div className="text-right text-sm">
          <div className="font-semibold">{formatCurrency(current)}</div>
          <div className="text-xs text-gray-500">TB: {formatCurrency(avg)}</div>
        </div>
      );
    },
  },
  {
    header: () => <div className="text-right">Vốn / AUM</div>,
    accessorKey: 'buy_value',
    cell: ({ getValue, row }) => {
      const buyValue = getValue();
      const marketValue = row.original.market_value;
      return (
        <div className="text-right text-sm">
          <div>{formatCurrency(buyValue)}</div>
          <div className="font-semibold">{formatCurrency(marketValue)}</div>
        </div>
      );
    },
  },
  {
    header: () => <div className="text-right">P&L</div>,
    accessorKey: 'unrealized_gain_loss_amount',
    cell: ({ getValue, row }) => {
      const amount = getValue();
      const percentage = row.original.unrealized_gain_loss_percentage;
      const isPositive = amount >= 0;
      return (
        <div className="text-right">
          <div
            className={`font-semibold ${
              isPositive ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {isPositive ? '+' : ''}
            {formatCurrency(amount)}
          </div>
          <div
            className={`text-xs ${
              isPositive ? 'text-green-500' : 'text-red-500'
            }`}
          >
            ({isPositive ? '+' : ''}
            {formatUnitQuantity(percentage)}%)
          </div>
        </div>
      );
    },
  },
  {
    header: 'T+N',
    accessorKey: 't0_buy_quantity',
    cell: ({ row }) => {
      const {
        t0_buy_quantity,
        t1_buy_quantity,
        t2_buy_quantity,
        t0_sell_quantity,
        t1_sell_quantity,
        t2_sell_quantity,
      } = row.original;
      const hasData =
        t0_buy_quantity ||
        t1_buy_quantity ||
        t2_buy_quantity ||
        t0_sell_quantity ||
        t1_sell_quantity ||
        t2_sell_quantity;

      if (!hasData) return '-';

      return (
        <div className="text-xs">
          <div className="grid grid-cols-2 gap-1">
            <div className="text-green-600">
              <div>T+0: {formatUnitQuantity(t0_buy_quantity || 0)}</div>
              <div>T+1: {formatUnitQuantity(t1_buy_quantity || 0)}</div>
              <div>T+2: {formatUnitQuantity(t2_buy_quantity || 0)}</div>
            </div>
            <div className="text-red-600">
              <div>T+0: {formatUnitQuantity(t0_sell_quantity || 0)}</div>
              <div>T+1: {formatUnitQuantity(t1_sell_quantity || 0)}</div>
              <div>T+2: {formatUnitQuantity(t2_sell_quantity || 0)}</div>
            </div>
          </div>
        </div>
      );
    },
  },
  {
    header: 'Cập nhật',
    accessorKey: 'updated_at',
    cell: dateTimeCell,
  },
];
